import * as tus from 'tus-js-client';

type UploadFileParams = {
  file: File,
  getToken: () => Promise<string>,
  onUnauthorized?: (failedToken: string) => Promise<boolean>,
  orderId: string,
  filename: string,
  partyId: string,
  archiveId: string
}

const tusUrl = import.meta.env.VITE_API_BASE_URL_FILES;

const UNAUTHORIZED = 401;

const isRetryableByDefault = (status: number) => (
  (!(status >= 400 && status < 500) || status === 409 || status === 423)
  && (typeof navigator === 'undefined' || navigator.onLine !== false)
);

export const uploadFile = async ({
  file,
  getToken,
  onUnauthorized,
  filename,
  orderId,
  partyId,
  archiveId
}: UploadFileParams) => new Promise((resolve, reject) => {
  let lastTokenUsed = '';
  let retryAfter401 = false;
  let unauthorizedHandled = false;

  const upload = new tus.Upload(file, {
    endpoint: tusUrl,
    metadata: {
      order_id: orderId,
      party_id: partyId,
      archive_id: archiveId,
      filename
    },
    onBeforeRequest: async (req) => {
      lastTokenUsed = await getToken();
      req.setHeader('Authorization', `Bearer ${lastTokenUsed}`);
    },
    onAfterResponse: async (_req, res) => {
      if (res.getStatus() !== UNAUTHORIZED || !onUnauthorized || unauthorizedHandled) return;
      unauthorizedHandled = true;
      try {
        retryAfter401 = await onUnauthorized(lastTokenUsed);
      } catch (error) {
        retryAfter401 = false;
      }
    },
    onShouldRetry: (error) => {
      const status = (error as tus.DetailedError).originalResponse?.getStatus() ?? 0;
      if (status === UNAUTHORIZED) {
        const shouldRetry = retryAfter401;
        retryAfter401 = false;
        return shouldRetry;
      }
      return isRetryableByDefault(status);
    },
    onError: (error) => {
      reject(error);
    },
    onSuccess: () => {
      resolve(file.name);
    }
  });
  upload.start();
});
